import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { LayoutDashboard, Megaphone, PlusCircle, ScanSearch, Wand2, Bot, Stethoscope, FileBarChart, Users, Plug, CreditCard, Settings, Shield, Menu, Moon, Sun, LogOut, Bell, Coins, HelpCircle, PanelLeftClose, PanelLeftOpen, Share2, Facebook, Instagram, Youtube, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { useScope } from "@/context/ScopeContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SubStatus } from "@/components/common/Badges";
import { api } from "@/lib/api";
import { dt } from "@/lib/format";
import { SUPPORT_EMAIL } from "@/lib/support";

const SECTIONS = [
  { title: "Negócio", items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
    { to: "/app/campaigns", label: "Campanhas", icon: Megaphone, end: true, id: "campaigns" },
    { to: "/app/campaigns/new", label: "Criar campanha", icon: PlusCircle, id: "create-campaign" },
    { to: "/app/clientes", label: "Clientes", icon: Users, id: "clients", agency: true },
    { to: "/app/relatorios", label: "Relatórios", icon: FileBarChart, id: "reports" },
  ] },
  { title: "Inteligência", items: [
    { to: "/app/assistente", label: "AI Assistant", icon: Bot, id: "assistant" },
    { to: "/app/optimizer", label: "Optimizer", icon: Wand2, id: "optimizer" },
    { to: "/app/optimizer?tab=analysis", label: "Analisar conta", icon: ScanSearch, id: "analyze" },
    { to: "/app/diagnostico", label: "Diagnóstico", icon: Stethoscope, id: "diagnostics" },
  ] },
  { title: "Canais de Anúncios", items: [
    { to: "/app/canais", label: "Conexões", icon: Share2, end: true, id: "channels" },
    { to: "/app/google-ads", label: "Google Ads", icon: Plug, id: "google-ads" },
    { to: "/app/canais/facebook", label: "Facebook Ads", icon: Facebook, id: "facebook-ads" },
    { to: "/app/canais/instagram", label: "Instagram Ads", icon: Instagram, id: "instagram-ads" },
    { to: "/app/canais/youtube", label: "YouTube Ads", icon: Youtube, id: "youtube-ads" },
    { to: "/app/canais/whatsapp", label: "WhatsApp Business", icon: MessageCircle, id: "whatsapp" },
  ] },
  { title: "Conta", items: [
    { to: "/app/assinatura", label: "Plano e faturamento", icon: CreditCard, id: "billing" },
    { to: "/app/conta", label: "Configurações", icon: Settings, id: "settings" },
  ] },
];
const TITLES = { "/app/canais": "Canais de Anúncios", "/app/canais/facebook": "Facebook Ads", "/app/canais/instagram": "Instagram Ads", "/app/canais/youtube": "YouTube Ads", "/app/canais/whatsapp": "WhatsApp Business", "/app": "Dashboard", "/app/campaigns": "Campanhas", "/app/campaigns/new": "Criar campanha", "/app/clientes": "Clientes", "/app/relatorios": "Relatórios", "/app/assistente": "AI Assistant", "/app/optimizer": "Optimizer", "/app/diagnostico": "Diagnóstico", "/app/google-ads": "Google Ads", "/app/assinatura": "Plano e faturamento", "/app/conta": "Configurações", "/admin": "Administração" };

const Item = ({ n, collapsed, onClick }) => (
  <NavLink to={n.to} end={n.end} onClick={onClick} data-testid={`nav-${n.id}`} title={n.label}
    className={({ isActive }) => `relative flex items-center gap-3 h-10 px-3 rounded-lg text-sm transition-colors ${isActive && !n.to.includes("?") ? "bg-accent text-accent-foreground font-medium before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r before:bg-primary" : "text-muted-foreground hover:bg-secondary hover:text-foreground"} ${collapsed ? "justify-center px-0" : ""}`}>
    <n.icon className="h-[18px] w-[18px] shrink-0" />{!collapsed && <span className="truncate">{n.label}</span>}
  </NavLink>
);

function Nav({ agency, admin, collapsed, onClick }) {
  const secs = SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => !i.agency || agency) }));
  if (admin) secs.push({ title: "Admin", items: [{ to: "/admin", label: "Painel admin", icon: Shield, id: "admin" }] });
  return secs.map((s) => (
    <div key={s.title} className="mb-5">
      {!collapsed && <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80">{s.title}</p>}
      <div className="flex flex-col gap-0.5">{s.items.map((n) => <Item key={n.id} n={n} collapsed={collapsed} onClick={onClick} />)}</div>
    </div>
  ));
}

function ScopeSelector() {
  const { clients, clientId, setClientId, accounts, accountId, setAccountId } = useScope() || {};
  const { orgInfo } = useAuth();
  return (
    <div className="flex items-center gap-2 min-w-0">
      {orgInfo?.org?.type === "agency" && (
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="client-selector" className="hidden md:flex h-9 w-[170px] text-xs"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Cliente: todos</SelectItem>{clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Select value={accountId || "none"} onValueChange={(v) => setAccountId(v === "none" ? "" : v)}>
        <SelectTrigger data-testid="account-selector" className="h-9 w-[130px] sm:w-[240px] text-xs"><SelectValue placeholder="Selecionar conta Google Ads" /></SelectTrigger>
        <SelectContent>
          {!accounts?.length && <SelectItem value="none">Nenhuma conta Google Ads</SelectItem>}
          {accounts?.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || "Conta"} · {a.customer_id}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const icon = { success: "text-[#34A853]", error: "text-[#EA4335]", warning: "text-[#F29900]", info: "text-primary" };
  return (
    <Popover onOpenChange={(o) => o && data.unread && api.post("/notifications/read-all").then(load)}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" aria-label="Notificações" className="relative h-9 w-9 grid place-items-center rounded-full hover:bg-secondary">
          <Bell className="h-[18px] w-[18px]" />
          {data.unread > 0 && <span data-testid="notifications-unread" className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#EA4335] text-[10px] text-white grid place-items-center">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b text-sm font-semibold">Notificações</div>
        <div className="max-h-96 overflow-auto divide-y">
          {!data.items.length && <p className="p-4 text-sm text-muted-foreground">Nada por aqui ainda.</p>}
          {data.items.map((n) => (
            <Link key={n.id} to={n.link || "#"} className="flex gap-3 px-4 py-3 hover:bg-secondary" data-testid="notification-item">
              <span className={`mt-1 h-2 w-2 rounded-full bg-current shrink-0 ${icon[n.kind] || icon.info}`} />
              <span><span className="block text-sm font-medium">{n.title}</span><span className="block text-xs text-muted-foreground mt-0.5">{n.message}</span><span className="block text-[10px] text-muted-foreground mt-1">{dt(n.created_at)}</span></span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  return (
    <button data-testid="theme-toggle" aria-label="Alternar tema" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="h-9 w-9 grid place-items-center rounded-full hover:bg-secondary">
      {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
    </button>
  );
};

export default function AppShell() {
  const { user, orgInfo, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(localStorage.getItem("cz_sidebar") === "1");
  const agency = orgInfo?.org?.type === "agency";
  const admin = user?.role === "admin";
  const org = orgInfo?.org;
  const out = async () => { await logout(); nav("/login"); };
  const toggle = () => { localStorage.setItem("cz_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const title = TITLES[loc.pathname] || (loc.pathname.startsWith("/app/campaigns/") ? "Campanha" : "");
  const initials = (user?.name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  const w = collapsed ? "lg:w-[72px]" : "lg:w-64";

  return (
    <div className="min-h-screen bg-background">
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 ${w} flex-col border-r bg-card px-3 py-5 transition-[width] duration-200 z-40`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} px-1 mb-7`}>
          {!collapsed && <Logo to="/app" sub />}
          <button onClick={toggle} data-testid="sidebar-toggle" aria-label="Recolher menu" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary text-muted-foreground">{collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}</button>
        </div>
        <nav className="flex-1 overflow-auto"><Nav agency={agency} admin={admin} collapsed={collapsed} /></nav>
        {!collapsed && org && (
          <Link to="/app/assinatura" data-testid="credits-indicator" className="rounded-lg border p-3 text-xs hover:bg-secondary">
            <div className="flex items-center justify-between"><span className="text-muted-foreground">Créditos de IA</span><Coins className="h-3.5 w-3.5 text-[#F29900]" /></div>
            <p className="text-lg font-semibold mt-0.5">{org.credits_balance}</p>
          </Link>
        )}
      </aside>
      <div className={collapsed ? "lg:pl-[72px]" : "lg:pl-64"}>
        <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b">
          <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 h-16">
            <div className="lg:hidden"><Logo to="/app" size={30} className="[&_.brand-text]:hidden sm:[&_.brand-text]:block" /></div>
            <h1 className="hidden lg:block text-lg font-semibold mr-2 whitespace-nowrap" data-testid="header-page-title">{title}</h1>
            <ScopeSelector />
            <div className="ml-auto flex items-center gap-1">
              {user?.role === "admin" && <Button size="sm" variant="outline" className="mr-1 rounded-full h-8 px-3 hidden sm:inline-flex" onClick={() => nav("/admin")} data-testid="topbar-admin-button"><Shield className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Painel admin</span></Button>}
              {org && <span className="hidden md:inline-flex mr-1"><SubStatus s={org.subscription_status} /></span>}
              <Notifications />
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button aria-label="Ajuda" data-testid="help-button" className="grid h-9 w-9 place-items-center rounded-full hover:bg-secondary"><HelpCircle className="h-[18px] w-[18px]" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="help-menu">
                  <DropdownMenuLabel><p className="text-sm">Precisa de ajuda?</p><p className="text-xs font-normal text-muted-foreground">Respondemos por email.</p></DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><a href={`mailto:${SUPPORT_EMAIL}`} data-testid="help-support-email">Suporte: {SUPPORT_EMAIL}</a></DropdownMenuItem>
                  <DropdownMenuItem asChild><a href="/#faq" data-testid="help-faq-link">Perguntas frequentes</a></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button data-testid="user-menu-button" aria-label="Conta" className="ml-1 h-8 w-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold grid place-items-center">{initials}</button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel><p className="text-sm">{user?.name}</p><p className="text-xs font-normal text-muted-foreground truncate">{user?.email}</p></DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user?.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="user-menu-admin"><Shield className="h-4 w-4 mr-2" />Painel admin</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => nav("/app/conta")}>Configurações</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/assinatura")}>Plano e faturamento</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={out} data-testid="logout-button" className="text-[#EA4335]"><LogOut className="h-4 w-4 mr-2" />Sair</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {(orgInfo?.announcements || []).slice(0, 1).map((a) => (
            <div key={a.id} data-testid="announcement-banner" className="px-4 sm:px-8 py-2 text-xs bg-accent text-accent-foreground"><b>{a.title}</b> — {a.message}</div>
          ))}
          {org && ["expired", "past_due", "suspended"].includes(org.subscription_status) && (
            <div data-testid="access-warning-banner" className="px-4 sm:px-8 py-2 text-xs bg-[#EA4335]/10 text-[#C5221F] dark:text-[#F28B82]">Seu acesso aos recursos de IA está limitado. <Link className="underline font-medium" to="/app/assinatura">Escolha um plano</Link>.</div>
          )}
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-28 lg:pb-8 max-w-[1440px]"><Outlet /></main>
      </div>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-card grid grid-cols-5 h-16 pb-[env(safe-area-inset-bottom)]">
        {[SECTIONS[0].items[0], SECTIONS[0].items[1], SECTIONS[1].items[0], { ...SECTIONS[2].items[0], label: "Canais" }].map((n) => (
          <NavLink key={n.id} to={n.to} end={n.end} data-testid={`mobile-nav-${n.id}`} className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] ${isActive ? "text-primary font-medium" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" />{n.label.replace("AI ", "")}
          </NavLink>
        ))}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button data-testid="mobile-nav-menu" className="flex flex-col items-center justify-center gap-1 text-[11px] text-muted-foreground"><Menu className="h-5 w-5" />Menu</button></SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-auto">
            <div className="pt-4"><Nav agency={agency} admin={admin} onClick={() => setOpen(false)} /></div>
            <button onClick={out} data-testid="mobile-logout-button" className="w-full flex items-center gap-3 px-3 h-11 rounded-lg text-sm text-[#EA4335]"><LogOut className="h-4 w-4" />Sair</button>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
